import { HttpMethods, makeRequest } from '../helpers/http.helper';
import type {
  CreateEmployeeTrainingBody,
  EmployeeTrainingListResult,
  EmployeeTrainingRecord,
  GenericResponse,
  OrganizationEmployeeTrainingSummary,
} from '../types/employee-training.type';

const EMPTY_SESSIONS_RESULT: EmployeeTrainingListResult = {
  total_count: 0,
  total_hours: 0,
  result_list: [],
};

const EMPTY_SUMMARY_RESULT: OrganizationEmployeeTrainingSummary = {
  result_list: [],
};

/** Backend sometimes omits `result` for empty lists — React Query forbids `undefined`. */
export function normalizeTrainingSessionsResult(
  res: GenericResponse<EmployeeTrainingListResult | EmployeeTrainingRecord[] | null> | null | undefined,
): EmployeeTrainingListResult {
  const result = res?.result;
  if (Array.isArray(result)) {
    return {
      total_count: result.length,
      total_hours: 0,
      result_list: result,
    };
  }
  if (!result || typeof result !== 'object') {
    return { ...EMPTY_SESSIONS_RESULT };
  }
  const result_list = Array.isArray(result.result_list) ? result.result_list : [];
  return {
    total_count: typeof result.total_count === 'number' ? result.total_count : result_list.length,
    total_hours: typeof result.total_hours === 'number' ? Number(result.total_hours) : 0,
    result_list,
  };
}

export function normalizeTrainingSummaryResult(
  res: GenericResponse<OrganizationEmployeeTrainingSummary | null> | null | undefined,
): OrganizationEmployeeTrainingSummary {
  const result = res?.result;
  if (!result || typeof result !== 'object') {
    return { ...EMPTY_SUMMARY_RESULT };
  }
  return {
    result_list: Array.isArray(result.result_list) ? result.result_list : [],
  };
}

export const getOrganizationTrainingSummary = (organizationId: string) => {
  return makeRequest({
    url: `/user/${organizationId}/training-summary`,
    method: HttpMethods.GET,
  }) as Promise<GenericResponse<OrganizationEmployeeTrainingSummary>>;
};

export const listEmployeeTrainingSessions = (
  organizationId: string,
  userId: string,
  params: { limit?: number; offset?: number } = {},
) => {
  return makeRequest({
    url: `/user/${organizationId}/${userId}/training-sessions`,
    method: HttpMethods.GET,
    params: {
      limit: params.limit,
      offset: params.offset,
    },
  }) as Promise<GenericResponse<EmployeeTrainingListResult>>;
};

export const createEmployeeTrainingSession = (
  organizationId: string,
  userId: string,
  body: CreateEmployeeTrainingBody,
) => {
  return makeRequest({
    url: `/user/${organizationId}/${userId}/training-sessions`,
    method: HttpMethods.POST,
    data: body,
  }) as Promise<GenericResponse<EmployeeTrainingRecord>>;
};
